import type * as THREE from 'three'
import {
  decodeAssemblyNodeIdSafely,
  normalizeAssemblyNodeId,
} from '../../assembly/utils'
import { resolveComponentInstanceTransformTarget } from '../../assembly/component-instance'
import type { ComposedAssemblyResult } from '../../imported-model/loaders'
import { ImportedModelObjectMetadataService } from '../../imported-model/metadata'
import {
  cloneLinearComponentPatternPersistenceDocument,
  parseLinearComponentPatternPersistenceDocument,
} from '../../pattern'
import type { LinearComponentPatternPersistenceDocument } from '../../pattern'

/**
 * 项目模型线性阵列组件根重绑器。
 *
 * 节点切换包可能仍以 display Mesh UUID 或稳定装配身份记录阵列源。本类只负责把这些运行时身份
 * 收敛到最终 detached 子树中的唯一 occurrence 组件根，不处理节点 alias 或拓扑引用。
 */
export class ProjectModelLinearComponentPatternComponentRootRebinder {
  /**
   * 在整包项目组合完成后重绑定根文档阵列。
   *
   * 包内物化发生在项目 occurrence 认领之前，不能直接把当时的 UUID 交给恢复器。
   * 本方法只返回独立定义，不挂载对象、不求解、不写入历史，也不改写记录已有的 owner；
   * 子装配 owner 的语义重定位仍由项目关系最终化器负责，不能一律覆盖成总装配根。
   *
   * @param sourceDocument 根包携带的阵列定义。
   * @param importedResult 已完成项目节点组合与场景索引建立的结果。
   * @returns 与最终组件根绑定的根文档阵列定义。
   */
  public rebindRootDocument(
    sourceDocument: LinearComponentPatternPersistenceDocument,
    importedResult: ComposedAssemblyResult,
  ): LinearComponentPatternPersistenceDocument {
    if (sourceDocument.records.length === 0) {
      return cloneLinearComponentPatternPersistenceDocument(sourceDocument)
    }
    const ownerAssemblyNodeId = importedResult.hierarchy.meta?.assemblyNodeId?.trim()
    if (!ownerAssemblyNodeId) {
      throw new Error('项目模型根文档线性阵列缺少最终装配根身份，无法重绑定组件源')
    }
    // 整包加载与局部节点替换共用同一套精确 UUID / assemblyNodeId 解析和 owner 范围校验。
    return this.rebind({ document: sourceDocument, importedResult, ownerAssemblyNodeId })
  }

  /**
   * 把阵列组件引用提升到最终 occurrence 组件根。
   *
   * @param input 已完成身份最终化的 detached 子树及待重绑阵列文档。
   * @returns 组件源只包含当前会话组件根 UUID 的独立文档。
   */
  public rebind(input: {
    readonly document: LinearComponentPatternPersistenceDocument
    readonly importedResult: ComposedAssemblyResult
    readonly ownerAssemblyNodeId: string
  }): LinearComponentPatternPersistenceDocument {
    const document = cloneLinearComponentPatternPersistenceDocument(input.document)
    document.records.forEach((record) => {
      const context = `线性阵列 owner=${record.ownerAssemblyNodeId ?? ''}, featureId=${record.featureId}`
      record.definition.state.componentObjectIds = record.definition.state.componentObjectIds.map(
        (objectId) => this.resolveComponentRoot(
          input.importedResult,
          input.ownerAssemblyNodeId,
          objectId,
          context,
        ).uuid,
      )
    })
    return parseLinearComponentPatternPersistenceDocument(document)
  }

  /**
   * 按 UUID 或稳定装配身份解析组件根。
   *
   * @param importedResult 已完成 sceneBindings 重建的 detached 子树。
   * @param ownerAssemblyNodeId 当前阵列文档所属的最终 package occurrence。
   * @param objectId 阵列文档中的运行时对象引用。
   * @param context 错误定位上下文。
   * @returns 唯一可写 occurrence 组件根。
   */
  private resolveComponentRoot(
    importedResult: ComposedAssemblyResult,
    ownerAssemblyNodeId: string,
    objectId: string,
    context: string,
  ): THREE.Object3D {
    const sceneBindings = importedResult.sceneBindings
    const findObjectById = (candidateObjectId: string): THREE.Object3D | null => (
      sceneBindings.byObject3DUuid[candidateObjectId]
      ?? importedResult.root.getObjectByProperty('uuid', candidateObjectId)
      ?? null
    )
    const resolveAssemblyNodeId = (object: THREE.Object3D): string => (
      ImportedModelObjectMetadataService.resolveAssemblyNodeIdFromSelfOrAncestors(object)?.trim() ?? ''
    )
    const isObjectInsideOwner = (object: THREE.Object3D): boolean => this.isNodeInsideOwner(
      resolveAssemblyNodeId(object),
      ownerAssemblyNodeId,
    )
    const stableCandidates = new Map<string, THREE.Object3D>()
    let hasExactStableIdentity = false
    Object.entries(sceneBindings.byAssemblyNodeId).forEach(([assemblyNodeId, boundObjectIds]) => {
      if (!this.areNodeIdsExactlyEquivalent(assemblyNodeId, objectId)) {
        return
      }
      hasExactStableIdentity = true
      boundObjectIds.forEach((boundObjectId) => {
        const object = findObjectById(boundObjectId)
        if (object && isObjectInsideOwner(object)) {
          stableCandidates.set(object.uuid, object)
        }
      })
    })

    // 已稳定化的 assemblyNodeId 是权威身份，必须先于 UUID 与旧 display 名称解析。
    if (hasExactStableIdentity) {
      return this.resolveUniqueComponentRoot(
        importedResult,
        findObjectById,
        stableCandidates,
        objectId,
        context,
      )
    }

    const candidates = new Map<string, THREE.Object3D>()
    const appendCandidate = (object: THREE.Object3D | null | undefined): void => {
      if (object && isObjectInsideOwner(object)) {
        candidates.set(object.uuid, object)
      }
    }

    const uuidObject = findObjectById(objectId)
    if (uuidObject) {
      appendCandidate(uuidObject)
      // UUID 属于权威运行时身份；命中 owner 外对象时必须拒绝。
      return this.resolveUniqueComponentRoot(
        importedResult,
        findObjectById,
        candidates,
        objectId,
        context,
      )
    }
    return this.resolveUniqueComponentRoot(
      importedResult,
      findObjectById,
      candidates,
      objectId,
      context,
    )
  }

  /**
   * 把候选对象统一解析为唯一组件根。
   *
   * @param importedResult 当前 detached 子树。
   * @param findObjectById 当前子树对象查询函数。
   * @param candidates 已按稳定身份或 owner 作用域筛选的候选对象。
   * @param objectId 原始阵列组件引用。
   * @param context 错误定位上下文。
   * @returns 唯一组件根。
   */
  private resolveUniqueComponentRoot(
    importedResult: ComposedAssemblyResult,
    findObjectById: (objectId: string) => THREE.Object3D | null,
    candidates: ReadonlyMap<string, THREE.Object3D>,
    objectId: string,
    context: string,
  ): THREE.Object3D {
    const componentRoots = new Map<string, THREE.Object3D>()
    candidates.forEach((candidate) => {
      const resolvedComponentRoot = resolveComponentInstanceTransformTarget(candidate, {
        scene: importedResult.root,
        getSceneObjectById: findObjectById,
      })
      // Finalizer 操作的是尚未入场的 detached 子树；它的根可以在挂载后成为真实组件根。
      const componentRoot = resolvedComponentRoot
        ?? (candidate === importedResult.root ? candidate : null)
      if (componentRoot) {
        componentRoots.set(componentRoot.uuid, componentRoot)
      }
    })
    if (componentRoots.size !== 1) {
      throw new Error(
        `项目模型局部替换 ${context} 组件源无法唯一解析：objectId=${objectId}, `
        + `candidates=${candidates.size}, roots=${componentRoots.size}`,
      )
    }
    return [...componentRoots.values()][0] as THREE.Object3D
  }

  /**
   * 判断两个节点 ID 是否仅在编码形式上不同。
   *
   * 这里不允许路径后缀或实例索引模糊匹配，因为阵列源必须锁定唯一 occurrence。
   *
   * @param left 左侧节点 ID。
   * @param right 右侧节点 ID。
   * @returns 规范化原值或安全解码值完全相同时返回 true。
   */
  private areNodeIdsExactlyEquivalent(left: string, right: string): boolean {
    const leftIds = new Set(this.collectExactNodeIds(left))
    return this.collectExactNodeIds(right).some((nodeId) => leftIds.has(nodeId))
  }

  /**
   * 判断节点是否位于当前阵列 owner occurrence 内。
   *
   * @param nodeId 候选对象装配身份。
   * @param ownerAssemblyNodeId 阵列 owner 装配身份。
   * @returns 候选等于 owner 或位于其后代路径时返回 true。
   */
  private isNodeInsideOwner(nodeId: string, ownerAssemblyNodeId: string): boolean {
    const nodeIds = this.collectExactNodeIds(nodeId)
    const ownerIds = this.collectExactNodeIds(ownerAssemblyNodeId)
    return nodeIds.some((candidateNodeId) => ownerIds.some((ownerNodeId) => (
      candidateNodeId === ownerNodeId || candidateNodeId.startsWith(`${ownerNodeId}/`)
    )))
  }

  /**
   * 收集节点 ID 的规范化原值与安全解码值。
   *
   * @param value 原始节点 ID。
   * @returns 不包含模糊实例索引或路径后缀的精确查找值。
   */
  private collectExactNodeIds(value: string): string[] {
    const normalized = normalizeAssemblyNodeId(value).trim()
    if (!normalized) {
      return []
    }
    const decoded = decodeAssemblyNodeIdSafely(normalized)
    return [normalized, decoded].filter((item, index, items) => (
      item.length > 0 && items.indexOf(item) === index
    ))
  }
}
